import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { QuestionWheel } from '@/components/seo/QuestionWheel';
import { QuestionWheelVisual } from '@/components/seo/QuestionWheelVisual';
import { SemanticMap } from '@/components/seo/SemanticMap';
import { QuestionGenerationProgressModal } from '@/components/seo/QuestionGenerationProgressModal';
import { QuestionProvenance } from '@/components/seo/QuestionProvenance';
import { Loader2, Search, TrendingUp, FileQuestion, List, Circle, RefreshCw, Trash2, TrendingDown, Target, MessageSquare, Star, Users, Network, GitBranch, Download, Bug, Wrench, Package, Sparkles, Scale, Filter, Copy, Eye } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Category definitions matching the auto-categorization script
const CATEGORY_DEFINITIONS = {
  'sector_tech': {
    name: 'Sector Technologies & Attributes',
    icon: Wrench,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-200 dark:border-blue-700'
  },
  'product_categories': {
    name: 'Product Categories',
    icon: Package,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
    borderColor: 'border-purple-200 dark:border-purple-700'
  },
  'benefits': {
    name: 'Perceived Benefits',
    icon: Sparkles,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    borderColor: 'border-green-200 dark:border-green-700'
  },
  'use_cases': {
    name: 'Use Cases & Applications',
    icon: Target,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30',
    borderColor: 'border-orange-200 dark:border-orange-700'
  },
  'comparison_topics': {
    name: 'Comparison Topics',
    icon: Scale,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/30',
    borderColor: 'border-indigo-200 dark:border-indigo-700'
  }
};

interface WheelHistory {
  id: string;
  center_term?: string;  // Site wheels
  name?: string;  // Sector wheels
  total_questions?: number;  // Site wheels
  question_count?: number;  // Sector wheels
  avg_search_volume?: number;
  avg_cpc?: string;
  created_at: string;
  mode?: 'sector' | 'product' | 'category';  // Generation mode
  wheel_type?: string;  // Sector wheels
  metadata?: {
    categories?: string[];
  };
}

export default function QuestionWheelResearch() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL params for sector and category filtering
  const searchParams = new URLSearchParams(location.search);
  const sectorIdParam = searchParams.get('sector');
  const categoryParam = searchParams.get('category');

  const [centerTerm, setCenterTerm] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [wheelData, setWheelData] = useState(null);
  const [wheelId, setWheelId] = useState<string | null>(null);
  const [wheelHistory, setWheelHistory] = useState<WheelHistory[]>([]);
  const [filteredWheelHistory, setFilteredWheelHistory] = useState<WheelHistory[]>([]);
  const [generationMode, setGenerationMode] = useState<'sector' | 'product' | 'category'>('sector');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);

  // Filtering and search
  const [selectedCategory, setSelectedCategory] = useState<string>(categoryParam || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWheelType, setSelectedWheelType] = useState<string>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const WHEELS_PER_PAGE = 12;
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);

  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressId, setProgressId] = useState<string | null>(null);
  const [generatingTerm, setGeneratingTerm] = useState<string>('');

  // Filter states
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [answerTypeFilter, setAnswerTypeFilter] = useState<string>('all');

  // Intent clustering state
  const [isClusterAnalyzing, setIsClusterAnalyzing] = useState(false);
  const [clusterAnalysis, setClusterAnalysis] = useState<any>(null);
  const [showClusterDialog, setShowClusterDialog] = useState(false);

  // Copy to site state
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [wheelToCopy, setWheelToCopy] = useState<{id: string, name: string} | null>(null);
  const [userSites, setUserSites] = useState<any[]>([]);
  const [selectedTargetSite, setSelectedTargetSite] = useState<string>('');
  const [isCopying, setIsCopying] = useState(false);

  // Current wheel categories for question-level categorization
  const [currentWheelCategories, setCurrentWheelCategories] = useState<string[]>([]);

  // Product mapping state
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [wheelToMap, setWheelToMap] = useState<{id: string, name: string} | null>(null);
  const [selectedMappingSite, setSelectedMappingSite] = useState<string>('');
  const [isMapping, setIsMapping] = useState(false);

  // Answer generation state
  const [showAnswerGenDialog, setShowAnswerGenDialog] = useState(false);
  const [wheelToGenerateAnswers, setWheelToGenerateAnswers] = useState<{id: string, name: string} | null>(null);
  const [selectedAnswerGenSite, setSelectedAnswerGenSite] = useState<string>('');
  const [isGeneratingAnswers, setIsGeneratingAnswers] = useState(false);

  // BP-PC-PB question generation state
  const [isGeneratingBpPcPb, setIsGeneratingBpPcPb] = useState(false);
  const [generatingBpPcPbWheelId, setGeneratingBpPcPbWheelId] = useState<string | null>(null);

  // NEW: Enhanced copy with tone customization
  const [copyLanguage, setCopyLanguage] = useState('es');
  const [copyStyle, setCopyStyle] = useState<'as-is' | 'professional' | 'casual'>('professional');

  // NEW: Product-specific question generation
  const [showProductQuestionsDialog, setShowProductQuestionsDialog] = useState(false);
  const [wheelForProductQuestions, setWheelForProductQuestions] = useState<{id: string, name: string, siteId: string} | null>(null);
  const [productSelection, setProductSelection] = useState<'all' | 'top_n' | 'specific'>('all');
  const [topNProducts, setTopNProducts] = useState(10);
  const [isGeneratingProductQuestions, setIsGeneratingProductQuestions] = useState(false);
  const [productQuestionProgress, setProductQuestionProgress] = useState(0);

  // NEW: Product answer generation
  const [showProductAnswersDialog, setShowProductAnswersDialog] = useState(false);
  const [wheelForProductAnswers, setWheelForProductAnswers] = useState<{id: string, name: string, siteId: string} | null>(null);
  const [answerQuality, setAnswerQuality] = useState<'high' | 'standard'>('high');
  const [isGeneratingProductAnswers, setIsGeneratingProductAnswers] = useState(false);
  const [productAnswerProgress, setProductAnswerProgress] = useState(0);

  // Handle incoming navigation state from Category Tree
  React.useEffect(() => {
    const state = location.state as any;
    if (state?.fromCategoryTree && state?.categoryName) {
      setCenterTerm(state.categoryName);

      if (state.wheelId) {
        // Load existing wheel
        loadHistoricalWheel(state.wheelId);
      } else if (state.autoGenerate) {
        // Auto-generate new wheel
        // Use setTimeout to ensure state is set before generation
        setTimeout(() => {
          generateWheelWithTerm(state.categoryName);
        }, 100);
      }

      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load wheel history when site/sector changes
  React.useEffect(() => {
    if (sectorIdParam) {
      // Load sector wheels
      loadWheelHistory();
    } else if (selectedSite?.id) {
      // Load site wheels
      loadWheelHistory();
    }
  }, [selectedSite?.id, sectorIdParam, categoryParam]);

  // Filter wheels based on category, search, and wheel type
  React.useEffect(() => {
    let filtered = [...wheelHistory];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(wheel => {
        const categories = wheel.metadata?.categories || [];
        return categories.includes(selectedCategory);
      });
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(wheel => {
        const name = (wheel.name || wheel.center_term || '').toLowerCase();
        return name.includes(search);
      });
    }

    // Filter by wheel type
    if (selectedWheelType !== 'all') {
      filtered = filtered.filter(wheel => {
        return wheel.wheel_type === selectedWheelType;
      });
    }

    setFilteredWheelHistory(filtered);
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [wheelHistory, selectedCategory, searchTerm, selectedWheelType]);

  const loadWheelHistory = async () => {
    setIsLoadingHistory(true);
    try {
      let response;
      let data;

      if (sectorIdParam) {
        // Load sector question wheels
        response = await authFetch(`/api/sectors/${sectorIdParam}/question-wheels`);
        const responseData = await response.json();

        // Extract wheels array from response
        data = responseData.wheels || [];
      } else if (selectedSite?.id) {
        // Load site question wheels
        response = await authFetch(`/api/sites/${selectedSite.id}/question-wheels`);
        data = await response.json();
      } else {
        setIsLoadingHistory(false);
        return;
      }

      setWheelHistory(data);
    } catch (error) {
      console.error('Error loading wheel history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const generateWheel = async () => {
    if (!centerTerm.trim() || !selectedSite?.id) {
      toast({
        title: 'Missing Information',
        description: 'Please enter a product or category name and select a site',
        variant: 'destructive'
      });
      return;
    }

    await generateWheelWithTerm(centerTerm.trim());
  };

  const generateWheelWithTerm = async (term: string) => {
    if (!selectedSite?.id) return;

    setIsGenerating(true);
    setGeneratingTerm(term);

    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/question-wheel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerTerm: term,
          options: {
            useEnhanced: true,  // Use Enhanced generator with native language support
            mode: generationMode,  // 'sector' (no customer data) | 'product' | 'category' (with customer data)
            location: 'United States',
            context: {}
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start question wheel generation');
      }

      const result = await response.json();

      // API now returns progressId immediately
      // Generation happens asynchronously with Socket.IO progress
      if (result.progressId) {
        setProgressId(result.progressId);
        setShowProgressModal(true);
      } else {
        // Fallback for old API format (shouldn't happen)
        setWheelData(result.data);
        toast({
          title: 'Success!',
          description: `Generated questions for "${term}"`,
        });
        loadWheelHistory();
      }

    } catch (error) {
      console.error('Error starting wheel generation:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to start question wheel generation',
        variant: 'destructive'
      });
      setIsGenerating(false);
    }
  };

  // Handle progress modal completion
  const handleGenerationComplete = async (wheelId: string, summary: any) => {
    // Load the generated wheel
    await loadHistoricalWheel(wheelId);

    // Refresh history
    loadWheelHistory();

    // Reset state
    setIsGenerating(false);

    toast({
      title: 'Success!',
      description: `Generated ${summary.totalQuestions} questions for "${generatingTerm}"`,
    });
  };

  // Handle progress modal close
  const handleProgressModalClose = () => {
    setShowProgressModal(false);
    setProgressId(null);
    setIsGenerating(false);
  };

  const loadHistoricalWheel = async (loadWheelId: string, isSector = false) => {
    try {
      let wheelData;
      let wheelName;

      if (isSector && sectorIdParam) {
        // Load sector wheel questions
        const questionsResponse = await authFetch(`/api/sectors/${sectorIdParam}/question-wheels/${loadWheelId}/questions`);
        const questionsData = await questionsResponse.json();

        // Find the wheel in our history to get its name and categories
        const wheel = wheelHistory.find(w => w.id === loadWheelId);
        wheelName = wheel?.name || 'Sector Wheel';

        // Extract categories from wheel metadata
        const categories = wheel?.metadata?.categories || [];
        setCurrentWheelCategories(categories);

        // Transform sector questions into wheel format
        const questions = questionsData.questions || [];

        // DEBUG: Log first question to see structure
        if (questions.length > 0) {
          console.log('DEBUG: First question structure:', JSON.stringify(questions[0], null, 2));
        }

        // Group questions by intent_type to create segments
        const segmentMap: Record<string, any[]> = {};
        questions.forEach((q: any, idx: number) => {
          const intent = q.intent_type || 'general';
          if (!segmentMap[intent]) {
            segmentMap[intent] = [];
          }
          const transformedQ = {
            text: q.question_text,  // PRIMARY field - QuestionWheel component uses this
            question_text: q.question_text,
            question: q.question_text,  // Some components use 'question'
            question_type: q.question_type || q.answer_type,
            answer_type: q.question_type || q.answer_type,  // Some components use 'answer_type'
            intent_type: q.intent_type,
            intent: q.intent_type,  // Some components use 'intent'
            target_persona: q.target_persona,
            priority: q.priority,
            search_volume: 0,  // Sector questions don't have search volume yet
            cpc: 0,
            metadata: q.metadata || {}
          };

          // DEBUG: Log first transformed question
          if (idx === 0) {
            console.log('DEBUG: First transformed question:', JSON.stringify(transformedQ, null, 2));
          }

          segmentMap[intent].push(transformedQ);
        });

        // Create segments array
        const segments = Object.entries(segmentMap).map(([intent, questions]) => ({
          name: intent.charAt(0).toUpperCase() + intent.slice(1),
          intent: intent,
          questions: questions
        }));

        wheelData = {
          centerTerm: wheelName,
          segments: segments,
          metadata: {
            isSectorWheel: true,
            totalQuestions: questions.length,
            sources: { sector_wheel: questions.length }
          }
        };
      } else {
        // Load regular site wheel
        const response = await authFetch(`/api/question-wheels/${loadWheelId}`);
        const data = await response.json();

        // JSONB data is already parsed by PostgreSQL
        wheelData = data.wheel.questions_data;
        wheelName = data.wheel.center_term;

        // Extract categories from wheel metadata (for site wheels)
        const categories = data.wheel.metadata?.categories || wheelData?.metadata?.categories || [];
        setCurrentWheelCategories(categories);

        // Normalize nested segments structure if needed
        if (wheelData?.segments?.segments && Array.isArray(wheelData.segments.segments)) {
          wheelData = {
            ...wheelData,
            segments: wheelData.segments.segments
          };
        }
      }

      setWheelData(wheelData);
      setWheelId(loadWheelId);
      setCenterTerm(wheelName);

      toast({
        title: 'Loaded',
        description: `Loaded question wheel for "${wheelName}"`,
      });
    } catch (error) {
      console.error('Error loading wheel:', error);
      toast({
        title: 'Load Failed',
        description: 'Failed to load question wheel',
        variant: 'destructive'
      });
    }
  };

  const regenerateWheel = async (wheelId: string, term: string) => {
    if (!selectedSite?.id) return;

    setRegeneratingId(wheelId);
    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/question-wheel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerTerm: term,
          options: {
            useEnhanced: true,  // Use Enhanced generator with native language support
            mode: generationMode,  // 'sector' (no customer data) | 'product' | 'category' (with customer data)
            location: 'United States',
            context: {}
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate question wheel');
      }

      const result = await response.json();
      setWheelData(result.data);
      setCenterTerm(term);

      toast({
        title: 'Regenerated!',
        description: `Generated ${result.data.totalQuestions} questions with latest improvements`,
      });

      // Refresh history
      loadWheelHistory();

    } catch (error) {
      console.error('Error regenerating wheel:', error);
      toast({
        title: 'Regeneration Failed',
        description: error.message || 'Failed to regenerate question wheel',
        variant: 'destructive'
      });
    } finally {
      setRegeneratingId(null);
    }
  };

  const deleteSectorWheel = async (wheelId: string, wheelName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${wheelName}"? This will remove all questions in this wheel.`)) {
      return;
    }

    setDeletingId(wheelId);
    try {
      const response = await authFetch(`/api/sectors/${sectorIdParam}/question-wheels/${wheelId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete sector question wheel');
      }

      toast({
        title: 'Deleted',
        description: `Removed "${wheelName}" from sector`,
      });

      // Refresh history
      loadWheelHistory();

      // Clear displayed wheel if it was the deleted one
      if (wheelData && (wheelData as any).name === wheelName) {
        setWheelData(null);
        setWheelId(null);
      }
    } catch (error: any) {
      console.error('Error deleting sector wheel:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete wheel',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const deleteWheel = async (wheelId: string, term: string) => {
    setDeletingId(wheelId);
    try {
      const response = await authFetch(`/api/question-wheels/${wheelId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete question wheel');
      }

      toast({
        title: 'Deleted',
        description: `Removed "${term}" from history`,
      });

      // Refresh history
      loadWheelHistory();

      // Clear displayed wheel if it was the deleted one
      if (wheelData && centerTerm === term) {
        setWheelData(null);
        setWheelId(null);
        setCenterTerm('');
      }

    } catch (error) {
      console.error('Error deleting wheel:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete question wheel',
        variant: 'destructive'
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Fetch user's sites when copy dialog opens
  const fetchUserSites = async () => {
    try {
      const response = await authFetch('/api/users/me/sites');
      if (!response.ok) throw new Error('Failed to fetch sites');
      const data = await response.json();
      setUserSites(data.sites || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sites',
        variant: 'destructive'
      });
    }
  };

  // Open copy dialog
  const openCopyDialog = (wheel: any) => {
    setWheelToCopy({ id: wheel.id, name: wheel.name || wheel.center_term });
    setShowCopyDialog(true);
    setSelectedTargetSite('');
    fetchUserSites();
  };

  // Copy wheel to site
  const copyWheelToSite = async () => {
    if (!wheelToCopy || !selectedTargetSite || !sectorIdParam) return;

    setIsCopying(true);
    try {
      const response = await authFetch(
        `/api/sectors/${sectorIdParam}/question-wheels/${wheelToCopy.id}/copy-to-site`,
        {
          method: 'POST',
          body: JSON.stringify({ siteId: selectedTargetSite })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to copy wheel');
      }

      const result = await response.json();

      const evaluationsInfo = result.evaluationsCount > 0
        ? ` and ${result.evaluationsCount} evaluation${result.evaluationsCount === 1 ? '' : 's'}`
        : '';

      toast({
        title: 'Success!',
        description: `Copied "${result.wheelName}" to ${result.siteName} with ${result.questionsCount} questions${evaluationsInfo}`,
      });

      setShowCopyDialog(false);
      setWheelToCopy(null);
      setSelectedTargetSite('');

    } catch (error: any) {
      console.error('Error copying wheel:', error);
      toast({
        title: 'Copy Failed',
        description: error.message || 'Failed to copy wheel to site',
        variant: 'destructive'
      });
    } finally {
      setIsCopying(false);
    }
  };

  // Product Mapping Functions
  const openMappingDialog = async (wheel: any) => {
    setWheelToMap({ id: wheel.id, name: wheel.name || wheel.center_term });

    // Fetch user sites if not already loaded
    if (userSites.length === 0) {
      await fetchUserSites();
    }

    setShowMappingDialog(true);
  };

  const openAnswerGenDialog = async (wheel: any) => {
    setWheelToGenerateAnswers({ id: wheel.id, name: wheel.name || wheel.center_term });

    // Fetch user sites if not already loaded
    if (userSites.length === 0) {
      await fetchUserSites();
    }

    setShowAnswerGenDialog(true);
  };

  // NEW: Product Questions Dialog Functions
  const openProductQuestionsDialog = async (wheel: any) => {
    setWheelForProductQuestions({
      id: wheel.id,
      name: wheel.name || wheel.center_term,
      siteId: selectedSite?.id || ''
    });

    // Fetch user sites if not already loaded
    if (userSites.length === 0) {
      await fetchUserSites();
    }

    setShowProductQuestionsDialog(true);
  };

  // NEW: Product Answers Dialog Functions
  const openProductAnswersDialog = async (wheel: any) => {
    setWheelForProductAnswers({
      id: wheel.id,
      name: wheel.name || wheel.center_term,
      siteId: selectedSite?.id || ''
    });

    // Fetch user sites if not already loaded
    if (userSites.length === 0) {
      await fetchUserSites();
    }

    setShowProductAnswersDialog(true);
  };

  // NEW: Export Enriched Feed Function
  const exportEnrichedFeed = async (wheelId: string, wheelName: string) => {
    if (!selectedSite?.id) {
      toast({
        title: 'Error',
        description: 'Please select a site first',
        variant: 'destructive'
      });
      return;
    }

    try {
      toast({
        title: 'Generating Export...',
        description: 'Preparing enriched feed export'
      });

      const response = await authFetch(
        `/api/sites/${selectedSite.id}/question-wheels/${wheelId}/export-feed`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            format: 'csv',
            includeStatus: ['approved', 'pending']
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export feed');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${wheelName.replace(/\s+/g, '-')}-enriched-feed.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success!',
        description: `Exported enriched feed for "${wheelName}"`
      });

    } catch (error: any) {
      console.error('Error exporting feed:', error);
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export enriched feed',
        variant: 'destructive'
      });
    }
  };

  // Generate BP-PC-PB questions
  const generateBpPcPbQuestions = async (wheelId: string, wheelName: string) => {
    if (!sectorIdParam) {
      toast({
        title: 'Error',
        description: 'Sector ID is required for BP-PC-PB question generation',
        variant: 'destructive'
      });
      return;
    }

    setIsGeneratingBpPcPb(true);
    setGeneratingBpPcPbWheelId(wheelId);

    try {
      const response = await authFetch(`/api/sectors/${sectorIdParam}/generate-bp-pc-pb-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionWheelId: wheelId,
          limit: 50 // Generate up to 50 questions
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate BP-PC-PB questions');
      }

      const data = await response.json();

      toast({
        title: 'Success!',
        description: `Generated ${data.questionsGenerated} BP-PC-PB questions for "${wheelName}"`,
      });

      // Refresh wheel history to show updated question count
      loadWheelHistory();

    } catch (error: any) {
      console.error('Error generating BP-PC-PB questions:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate BP-PC-PB questions',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingBpPcPb(false);
      setGeneratingBpPcPbWheelId(null);
    }
  };

  const mapWheelToProducts = async () => {
    if (!wheelToMap || !selectedMappingSite || !sectorIdParam) return;

    setIsMapping(true);
    try {
      const response = await authFetch(
        `/api/sectors/${sectorIdParam}/question-wheels/${wheelToMap.id}/map-to-products`,
        {
          method: 'POST',
          body: JSON.stringify({
            siteId: selectedMappingSite,
            similarityThreshold: 0.50,
            strategy: 'semantic'
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to map questions to products');
      }

      const result = await response.json();
      toast({
        title: 'Mapping Complete!',
        description: `Created ${result.mappingsCreated} question-product mappings for ${result.siteName}. Processed ${result.questionsProcessed} questions.`,
      });

      setShowMappingDialog(false);
      setWheelToMap(null);
      setSelectedMappingSite('');

    } catch (error: any) {
      console.error('Error mapping wheel to products:', error);
      toast({
        title: 'Mapping Failed',
        description: error.message || 'Failed to map questions to products',
        variant: 'destructive'
      });
    } finally {
      setIsMapping(false);
    }
  };

  const generateCategoryAnswers = async () => {
    if (!wheelToGenerateAnswers || !selectedAnswerGenSite || !sectorIdParam) return;

    setIsGeneratingAnswers(true);
    try {
      const response = await authFetch(
        `/api/sectors/${sectorIdParam}/question-wheels/${wheelToGenerateAnswers.id}/generate-category-answers`,
        {
          method: 'POST',
          body: JSON.stringify({
            siteId: selectedAnswerGenSite,
            maxProductsPerAnswer: 5
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate answers');
      }

      const result = await response.json();
      toast({
        title: 'Answer Generation Complete!',
        description: result.message,
      });

      setShowAnswerGenDialog(false);
      setWheelToGenerateAnswers(null);
      setSelectedAnswerGenSite('');

    } catch (error: any) {
      console.error('Error generating answers:', error);
      toast({
        title: 'Answer Generation Failed',
        description: error.message || 'Failed to generate answers',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingAnswers(false);
    }
  };

  const exportWheel = (format: 'csv' | 'json') => {
    if (!wheelData) return;

    if (format === 'csv') {
      // Create CSV from all questions
      const allQuestions: any[] = [];
      wheelData.segments.forEach(segment => {
        segment.questions.forEach(q => {
          allQuestions.push({
            type: segment.type,
            question: q.text,
            search_volume: q.search_volume,
            cpc: q.cpc,
            opportunity_score: q.opportunity_score,
            difficulty_score: q.difficulty_score,
            sources: q.sources?.join(';') || '',
            source_count: q.source_count || 0,
            answer_type: q.answer_type || '',
            answer_type_label: q.answer_type_label || '',
            answer_type_confidence: q.answer_type_confidence || '',
            answer_type_reasoning: q.answer_type_reasoning || '',
            answer_type_subdomain: q.answer_type_subdomain || ''
          });
        });
      });

      const headers = [
        'Type', 'Question', 'Search Volume', 'CPC', 'Opportunity Score', 'Difficulty Score',
        'Sources', 'Source Count', 'Answer Type', 'Answer Type Label', 'Confidence', 'Reasoning', 'Subdomain'
      ];
      const csvContent = [
        headers.join(','),
        ...allQuestions.map(q =>
          `${q.type},"${q.question.replace(/"/g, '""')}",${q.search_volume},${q.cpc},${q.opportunity_score},${q.difficulty_score},"${q.sources}",${q.source_count},"${q.answer_type}","${q.answer_type_label}",${q.answer_type_confidence},"${q.answer_type_reasoning.replace(/"/g, '""')}","${q.answer_type_subdomain}"`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `question-wheel-${centerTerm}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else {
      // Export as JSON
      const blob = new Blob([JSON.stringify(wheelData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `question-wheel-${centerTerm}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    }

    toast({
      title: 'Exported',
      description: `Question wheel exported as ${format.toUpperCase()}`,
    });
  };

  // Run intent clustering analysis
  const runIntentClustering = async () => {
    if (!wheelId) {
      toast({
        title: 'Error',
        description: 'No wheel loaded',
        variant: 'destructive',
      });
      return;
    }

    setIsClusterAnalyzing(true);
    try {
      const response = await authFetch(
        `/api/question-wheels/${wheelId}/cluster-analysis`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to run clustering analysis');
      }

      const data = await response.json();
      setClusterAnalysis(data);
      setShowClusterDialog(true);

      toast({
        title: 'Intent Clustering Complete',
        description: `Found ${data.intent_clusters?.length || 0} intent clusters`,
      });
    } catch (error) {
      console.error('Error running intent clustering:', error);
      toast({
        title: 'Error',
        description: 'Failed to run intent clustering analysis',
        variant: 'destructive',
      });
    } finally {
      setIsClusterAnalyzing(false);
    }
  };

  // Filter wheel data based on source and answer type filters
  const filteredWheelData = React.useMemo(() => {
    if (!wheelData) return null;

    let filteredSegments = wheelData.segments.map(segment => ({
      ...segment,
      questions: segment.questions.filter(q => q.status !== 'duplicate')
    }));

    // Apply source filter
    if (sourceFilter !== 'all') {
      filteredSegments = filteredSegments.map(segment => ({
        ...segment,
        questions: segment.questions.filter(q => {
          if (sourceFilter === 'reviews') return q.sources?.includes('reviews');
          if (sourceFilter === 'google_paa') return q.sources?.includes('google_paa');
          if (sourceFilter === 'multi-source') return (q.source_count || 0) >= 2;
          if (sourceFilter === 'validated') return q.sources?.includes('reviews') && q.sources?.includes('google_paa');
          return true;
        })
      }));
    }

    // Apply answer type filter
    if (answerTypeFilter !== 'all') {
      filteredSegments = filteredSegments.map(segment => ({
        ...segment,
        questions: segment.questions.filter(q => q.answer_type === answerTypeFilter)
      }));
    }

    // Remove empty segments and recalculate totals
    filteredSegments = filteredSegments.filter(s => s.questions.length > 0);
    const totalFilteredQuestions = filteredSegments.reduce((sum, s) => sum + s.questions.length, 0);

    return {
      ...wheelData,
      segments: filteredSegments,
      totalQuestions: totalFilteredQuestions
    };
  }, [wheelData, sourceFilter, answerTypeFilter]);

  // Calculate filter statistics
  const filterStats = React.useMemo(() => {
    if (!wheelData) return { reviewCount: 0, paaCount: 0, multiSourceCount: 0, reviewValidated: 0, answerTypeStats: {} };

    const allQuestions = wheelData.segments.flatMap(s => s.questions.filter(q => q.status !== 'duplicate'));
    const reviewCount = allQuestions.filter(q => q.sources?.includes('reviews')).length;
    const paaCount = allQuestions.filter(q => q.sources?.includes('google_paa')).length;
    const multiSourceCount = allQuestions.filter(q => (q.source_count || 0) >= 2).length;
    const reviewValidated = allQuestions.filter(q =>
      q.sources?.includes('reviews') && q.sources?.includes('google_paa')
    ).length;

    // Calculate answer type statistics
    const answerTypeStats: { [key: string]: number } = {};
    allQuestions.forEach(q => {
      if (q.answer_type) {
        answerTypeStats[q.answer_type] = (answerTypeStats[q.answer_type] || 0) + 1;
      }
    });

    return { reviewCount, paaCount, multiSourceCount, reviewValidated, answerTypeStats };
  }, [wheelData]);

  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardHeader>
            <CardTitle>No Site Selected</CardTitle>
            <CardDescription>Please select a site from the header to use Question Wheel Research</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto py-6 space-y-6 px-4">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileQuestion className="h-8 w-8" />
          Question Wheel Generator
        </h1>
        <p className="text-muted-foreground mt-2">
          Generate comprehensive question analysis for products and categories to discover content opportunities
        </p>
      </div>

      {/* Generation Form */}
      <Card>
        <CardHeader>
          <CardTitle>Generate Question Wheel</CardTitle>
          <CardDescription>
            Enter a product name or category to analyze question opportunities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Generation Mode Selector */}
          <div className="mb-4 p-3 bg-muted rounded-lg">
            <Label className="text-sm font-medium mb-2 block">Generation Mode</Label>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                variant={generationMode === 'sector' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGenerationMode('sector')}
                className={generationMode === 'sector' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <TrendingUp className="h-3 w-3 mr-1" />
                Sector Intelligence
              </Button>
              <Button
                type="button"
                variant={generationMode === 'product' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGenerationMode('product')}
                className={generationMode === 'product' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <FileQuestion className="h-3 w-3 mr-1" />
                Product Wheel
              </Button>
              <Button
                type="button"
                variant={generationMode === 'category' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGenerationMode('category')}
                className={generationMode === 'category' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                <List className="h-3 w-3 mr-1" />
                Category Wheel
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {generationMode === 'sector' && '🔒 Pure sector intelligence - Excludes customer reviews & Google Ads (reusable across all customers)'}
              {generationMode === 'product' && '✅ Sector intelligence + Customer reviews + Google Ads search terms (product-specific)'}
              {generationMode === 'category' && '✅ Sector intelligence + Category reviews + Google Ads data (category-specific)'}
            </p>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="centerTerm">Product or Category</Label>
              <Input
                id="centerTerm"
                placeholder="e.g., running shoes, coffee maker, laptop..."
                value={centerTerm}
                onChange={(e) => setCenterTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && generateWheel()}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={generateWheel} disabled={isGenerating || !centerTerm.trim()}>
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wheel History */}
      {wheelHistory.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {sectorIdParam ? 'Sector Question Wheels' : 'Previous Analyses'}
                </CardTitle>
                <CardDescription>
                  {sectorIdParam
                    ? `Showing ${filteredWheelHistory.length} of ${wheelHistory.length} wheels`
                    : 'Load a previously generated question wheel'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 pb-4 border-b">
              {/* Search */}
              <div>
                <Label htmlFor="search" className="text-xs">Search</Label>
                <Input
                  id="search"
                  placeholder="Search wheels..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Category Filter */}
              <div>
                <Label htmlFor="category" className="text-xs">Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="sector_tech">Sector Technologies</SelectItem>
                    <SelectItem value="product_categories">Product Categories</SelectItem>
                    <SelectItem value="benefits">Perceived Benefits</SelectItem>
                    <SelectItem value="use_cases">Use Cases</SelectItem>
                    <SelectItem value="comparison_topics">Comparison Topics</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Wheel Type Filter */}
              {sectorIdParam && (
                <div>
                  <Label htmlFor="wheel-type" className="text-xs">Wheel Type</Label>
                  <Select value={selectedWheelType} onValueChange={setSelectedWheelType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="attribute">Attribute</SelectItem>
                      <SelectItem value="benefit">Benefit</SelectItem>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="comparison">Comparison</SelectItem>
                      <SelectItem value="use_case">Use Case</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Wheel Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {filteredWheelHistory
                .slice((currentPage - 1) * WHEELS_PER_PAGE, currentPage * WHEELS_PER_PAGE)
                .map(wheel => {
                const wheelName = wheel.name || wheel.center_term || 'Unnamed';
                const questionCount = wheel.question_count || wheel.total_questions || 0;
                const categories = wheel.metadata?.categories || [];
                const isSectorWheel = !!wheel.name; // Sector wheels have 'name' field

                return (
                <div
                  key={wheel.id}
                  className="border dark:border-gray-600 rounded-lg p-3 hover:bg-accent dark:hover:bg-gray-700 transition-colors cursor-pointer bg-white dark:bg-gray-800"
                  onClick={() => loadHistoricalWheel(wheel.id, isSectorWheel)}
                >
                  <div>
                    {/* Wheel Name */}
                    <div className="font-medium mb-2 dark:text-gray-100">{wheelName}</div>

                    {/* Category Badges */}
                    {categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {categories.map(category => {
                          const def = CATEGORY_DEFINITIONS[category as keyof typeof CATEGORY_DEFINITIONS];
                          if (!def) return null;
                          const Icon = def.icon;
                          return (
                            <Badge
                              key={category}
                              variant="outline"
                              className={`${def.bgColor} ${def.borderColor} text-xs`}
                            >
                              <Icon className={`h-3 w-3 mr-1 ${def.color}`} />
                              {def.name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Wheel Type Badge (for sector wheels) */}
                    {wheel.wheel_type && (
                      <div className="mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {wheel.wheel_type}
                        </Badge>
                      </div>
                    )}

                    {/* Mode Badge (for site wheels) */}
                    {wheel.mode && !isSectorWheel && (
                      <div className="mb-2">
                        <Badge
                          variant={wheel.mode === 'sector' ? 'secondary' : wheel.mode === 'product' ? 'default' : 'outline'}
                          className={
                            wheel.mode === 'sector' ? 'bg-blue-100 text-blue-800' :
                            wheel.mode === 'product' ? 'bg-green-100 text-green-800' :
                            'bg-purple-100 text-purple-800'
                          }
                        >
                          {wheel.mode === 'sector' ? '🔒 Sector' : wheel.mode === 'product' ? '✅ Product' : '✅ Category'}
                        </Badge>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                      {questionCount} questions
                      {wheel.avg_search_volume && ` • Avg volume: ${wheel.avg_search_volume?.toLocaleString()}`}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(wheel.created_at).toLocaleString()}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-2 pt-2 border-t dark:border-gray-600 flex flex-col gap-1">
                    {/* First row - Main actions */}
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs h-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          const debugUrl = isSectorWheel && sectorIdParam
                            ? `/question-wheels/${wheel.id}/debug?sector=${sectorIdParam}`
                            : `/question-wheels/${wheel.id}/debug`;
                          navigate(debugUrl);
                        }}
                      >
                        <Bug className="h-3 w-3 mr-1" />
                        Evaluate
                      </Button>

                      {user?.role === 'site_content_manager' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            loadHistoricalWheel(wheel.id, isSectorWheel);
                          }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      )}

                      {user?.role !== 'site_content_manager' && isSectorWheel && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs h-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCopyDialog(wheel);
                            }}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy
                          </Button>
                        </>
                      )}
                    </div>

                    {/* NEW ENHANCED WORKFLOW - Second row (for sector wheels) */}
                    {user?.role !== 'site_content_manager' && isSectorWheel && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          className="flex-1 text-xs h-7 bg-primary hover:bg-primary/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProductQuestionsDialog(wheel);
                          }}
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          Product Q
                          <Badge className="ml-1 text-[10px] px-1 py-0 h-4 bg-primary/10 text-primary border-primary/20">⚡</Badge>
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 text-xs h-7 bg-primary hover:bg-primary/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProductAnswersDialog(wheel);
                          }}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Product A
                          <Badge className="ml-1 text-[10px] px-1 py-0 h-4 bg-primary/10 text-primary border-primary/20">⚡</Badge>
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 text-xs h-7 bg-primary hover:bg-primary/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportEnrichedFeed(wheel.id, wheel.name || wheel.center_term);
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Export
                          <Badge className="ml-1 text-[10px] px-1 py-0 h-4 bg-primary/10 text-primary border-primary/20">⚡</Badge>
                        </Button>
                      </div>
                    )}

                    {/* LEGACY FEATURES - Third row (for sector wheels) */}
                    {user?.role !== 'site_content_manager' && isSectorWheel && (
                      <div className="flex gap-1 opacity-70">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMappingDialog(wheel);
                          }}
                        >
                          <Network className="h-3 w-3 mr-1" />
                          Map
                          <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0 h-4">Legacy</Badge>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAnswerGenDialog(wheel);
                          }}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Answers
                          <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0 h-4">Legacy</Badge>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateBpPcPbQuestions(wheel.id, wheel.name || wheel.center_term);
                          }}
                          disabled={isGeneratingBpPcPb && generatingBpPcPbWheelId === wheel.id}
                        >
                          {isGeneratingBpPcPb && generatingBpPcPbWheelId === wheel.id ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Gen...
                            </>
                          ) : (
                            <>
                              <Users className="h-3 w-3 mr-1" />
                              BP-PC-PB
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Delete button - Fourth row (for sector wheels) */}
                    {user?.role !== 'site_content_manager' && isSectorWheel && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1 text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSectorWheel(wheel.id, wheelName);
                          }}
                          disabled={deletingId === wheel.id}
                        >
                          {deletingId === wheel.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Regenerate and Delete - only for site wheels */}
                    {!isSectorWheel && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedSite?.id) {
                              navigate(`/sites/${selectedSite.id}/wheels/${wheel.id}/generate-product-questions`);
                            }
                          }}
                        >
                          <Package className="h-3 w-3 mr-1" />
                          Products
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            regenerateWheel(wheel.id, wheelName);
                        }}
                          disabled={regeneratingId === wheel.id || deletingId === wheel.id}
                        >
                          {regeneratingId === wheel.id ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Regenerate
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteWheel(wheel.id, wheelName);
                          }}
                          disabled={deletingId === wheel.id || regeneratingId === wheel.id}
                        >
                          {deletingId === wheel.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {filteredWheelHistory.length > WHEELS_PER_PAGE && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * WHEELS_PER_PAGE) + 1} to {Math.min(currentPage * WHEELS_PER_PAGE, filteredWheelHistory.length)} of {filteredWheelHistory.length} wheels
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.ceil(filteredWheelHistory.length / WHEELS_PER_PAGE) }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first, last, current, and pages around current
                        const totalPages = Math.ceil(filteredWheelHistory.length / WHEELS_PER_PAGE);
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, idx, array) => (
                        <React.Fragment key={page}>
                          {idx > 0 && array[idx - 1] !== page - 1 && (
                            <span className="px-2 text-muted-foreground">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="w-10"
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredWheelHistory.length / WHEELS_PER_PAGE), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredWheelHistory.length / WHEELS_PER_PAGE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Question Wheel Visualization */}
      {wheelData && filteredWheelData && (
        <>
          {/* Filters - Shown above both tabs */}
          <Card>
            <CardContent className="pt-6">
              {/* Source Filter */}
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-muted-foreground">Filter by source:</span>
                  <div className="flex gap-1 flex-wrap">
                    <Button
                      variant={sourceFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSourceFilter('all')}
                    >
                      All Sources
                    </Button>
                    {filterStats.reviewCount > 0 && (
                      <Button
                        variant={sourceFilter === 'reviews' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSourceFilter('reviews')}
                        className={sourceFilter === 'reviews' ? 'bg-green-600 hover:bg-green-700' : ''}
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Customer Reviews ({filterStats.reviewCount})
                      </Button>
                    )}
                    {filterStats.paaCount > 0 && (
                      <Button
                        variant={sourceFilter === 'google_paa' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSourceFilter('google_paa')}
                        className={sourceFilter === 'google_paa' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      >
                        <Search className="h-3 w-3 mr-1" />
                        Google PAA ({filterStats.paaCount})
                      </Button>
                    )}
                    {filterStats.multiSourceCount > 0 && (
                      <Button
                        variant={sourceFilter === 'multi-source' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSourceFilter('multi-source')}
                        className={sourceFilter === 'multi-source' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                      >
                        <Star className="h-3 w-3 mr-1" />
                        Multi-Source ({filterStats.multiSourceCount})
                      </Button>
                    )}
                    {filterStats.reviewValidated > 0 && (
                      <Button
                        variant={sourceFilter === 'validated' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSourceFilter('validated')}
                        className={sourceFilter === 'validated' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      >
                        <Users className="h-3 w-3 mr-1" />
                        Customer + Search ({filterStats.reviewValidated})
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Answer Type Filter */}
              {Object.keys(filterStats.answerTypeStats).length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-muted-foreground">Filter by answer type:</span>
                    <div className="flex gap-1 flex-wrap">
                      <Button
                        variant={answerTypeFilter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAnswerTypeFilter('all')}
                      >
                        All Types
                      </Button>
                      {Object.entries(filterStats.answerTypeStats).map(([type, count]) => (
                        <Button
                          key={type}
                          variant={answerTypeFilter === type ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setAnswerTypeFilter(type)}
                        >
                          {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} ({count})
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions Row */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Actions:</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportWheel('csv')}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={runIntentClustering}
                      disabled={isClusterAnalyzing || !wheelId}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isClusterAnalyzing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <GitBranch className="h-4 w-4 mr-2" />
                          Run Intent Clustering
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="visual" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3">
              <TabsTrigger value="visual" className="flex items-center gap-2">
                <Circle className="h-4 w-4" />
                Visual Wheel
              </TabsTrigger>
              <TabsTrigger value="list" className="flex items-center gap-2">
                <List className="h-4 w-4" />
                Question List
              </TabsTrigger>
              <TabsTrigger value="semantic" className="flex items-center gap-2">
                <Network className="h-4 w-4" />
                Semantic Map
              </TabsTrigger>
            </TabsList>

            <TabsContent value="visual" className="mt-6">
              <QuestionWheelVisual
                centerTerm={centerTerm}
                data={filteredWheelData}
                onQuestionClick={(question) => {
                  setSelectedQuestion(question);
                  setShowQuestionDialog(true);
                }}
              />
            </TabsContent>

            <TabsContent value="list" className="mt-6">
              <QuestionWheel
                centerTerm={centerTerm}
                data={filteredWheelData}
                wheelCategories={currentWheelCategories}
                onQuestionClick={(question) => {
                  console.log('Question clicked:', question);
                  toast({
                    title: question.text,
                    description: `Volume: ${question.search_volume.toLocaleString()} | CPC: $${question.cpc} | Opportunity: ${question.opportunity_score}`,
                  });
                }}
                onExport={exportWheel}
              />
            </TabsContent>

            <TabsContent value="semantic" className="mt-6">
              <SemanticMap
                wheelId={wheelId || ''}
                siteId={selectedSite.id}
                sectorId={sectorIdParam}
                centerTerm={centerTerm}
                sourceFilter={sourceFilter}
                answerTypeFilter={answerTypeFilter}
                onQuestionClick={(question) => {
                  setSelectedQuestion(question);
                  setShowQuestionDialog(true);
                }}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Empty State */}
      {!wheelData && !isGenerating && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Question Wheel Generated Yet</h3>
            <p className="text-muted-foreground text-center max-w-md">
              Enter a product or category name above and click "Generate" to create a comprehensive
              question analysis with search volume, CPC, and opportunity scores.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Question Details Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">{selectedQuestion?.text}</DialogTitle>
            <DialogDescription>Question analysis and metrics</DialogDescription>
          </DialogHeader>
          {selectedQuestion && (
            <div className="space-y-4">
              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Search Volume</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {selectedQuestion.search_volume?.toLocaleString() || 'N/A'}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Monthly searches</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Cost Per Click</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ${selectedQuestion.cpc || '0.00'}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Average CPC</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Opportunity Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <div className="text-2xl font-bold">
                        {selectedQuestion.opportunity_score}
                      </div>
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Content opportunity</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Difficulty Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <div className="text-2xl font-bold">
                        {selectedQuestion.difficulty_score}
                      </div>
                      <TrendingDown className="h-5 w-5 text-orange-500" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Ranking difficulty</p>
                  </CardContent>
                </Card>
              </div>

              {/* Question Type Badge */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Question Type</Label>
                <Badge variant="outline" className="text-sm">
                  {selectedQuestion.type}
                </Badge>
              </div>

              {/* Category Badges */}
              {currentWheelCategories && currentWheelCategories.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {currentWheelCategories.map(category => {
                      const def = CATEGORY_DEFINITIONS[category as keyof typeof CATEGORY_DEFINITIONS];
                      if (!def) return null;
                      const Icon = def.icon;
                      return (
                        <Badge
                          key={category}
                          variant="outline"
                          className={`${def.bgColor} ${def.borderColor}`}
                        >
                          <Icon className={`h-3 w-3 mr-1 ${def.color}`} />
                          {def.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* SERP Features */}
              {(selectedQuestion.has_featured_snippet || selectedQuestion.has_paa || selectedQuestion.has_video_results || selectedQuestion.has_image_pack) && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">SERP Features</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedQuestion.has_featured_snippet && (
                      <Badge variant="secondary" className="text-xs">
                        Featured Snippet{selectedQuestion.featured_snippet_type ? ` (${selectedQuestion.featured_snippet_type})` : ''}
                      </Badge>
                    )}
                    {selectedQuestion.has_paa && (
                      <Badge variant="secondary" className="text-xs">People Also Ask</Badge>
                    )}
                    {selectedQuestion.has_video_results && (
                      <Badge variant="secondary" className="text-xs">Video Results</Badge>
                    )}
                    {selectedQuestion.has_image_pack && (
                      <Badge variant="secondary" className="text-xs">Image Pack</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* People Also Ask Questions */}
              {selectedQuestion.paa_questions && selectedQuestion.paa_questions.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Related PAA Questions</Label>
                  <div className="space-y-1">
                    {selectedQuestion.paa_questions.map((paaQuestion: string, idx: number) => (
                      <div key={idx} className="text-sm text-muted-foreground pl-3 border-l-2 border-muted">
                        {paaQuestion}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Ranking Domains */}
              {selectedQuestion.top_domains && selectedQuestion.top_domains.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Top Ranking Domains</Label>
                  <div className="space-y-2">
                    {selectedQuestion.top_domains.map((domain: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="text-xs">{domain.position}</Badge>
                        <span className="font-medium">{domain.domain}</span>
                        <span className="text-muted-foreground truncate flex-1">{domain.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Searches */}
              {selectedQuestion.related_searches && selectedQuestion.related_searches.length > 0 && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Related Searches</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedQuestion.related_searches.map((search: string, idx: number) => (
                      <Badge key={idx} variant="outline" className="text-xs">{search}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Content Gap Analysis */}
              {selectedQuestion.content_gap && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Content Gap Analysis</Label>
                  <div className="rounded-md bg-muted p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        {selectedQuestion.content_gap === 'low_coverage' && '🟢 Low Coverage - Great Opportunity!'}
                        {selectedQuestion.content_gap === 'medium_coverage' && '🟡 Medium Coverage - Competitive'}
                        {selectedQuestion.content_gap === 'high_coverage' && '🔴 High Coverage - Saturated'}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        Saturation: {selectedQuestion.serp_saturation || 0}%
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedQuestion.content_gap === 'low_coverage' &&
                        'This topic has limited existing content. You have a strong opportunity to rank with quality content.'}
                      {selectedQuestion.content_gap === 'medium_coverage' &&
                        'This topic has moderate competition. Focus on unique angles and comprehensive coverage to stand out.'}
                      {selectedQuestion.content_gap === 'high_coverage' &&
                        'This topic is well-covered by existing content. Consider targeting long-tail variations or unique perspectives.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Analysis Notes */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Recommendation</Label>
                <div className="rounded-md bg-muted p-3 text-sm">
                  {selectedQuestion.opportunity_score >= 70 && (
                    <p className="text-green-700 dark:text-green-400">
                      <strong>High Opportunity:</strong> This question has strong search volume with reasonable competition. Consider creating dedicated content to target this query.
                    </p>
                  )}
                  {selectedQuestion.opportunity_score >= 40 && selectedQuestion.opportunity_score < 70 && (
                    <p className="text-yellow-700 dark:text-yellow-400">
                      <strong>Medium Opportunity:</strong> Decent search potential but may require more effort to rank. Good candidate for secondary content or FAQ sections.
                    </p>
                  )}
                  {selectedQuestion.opportunity_score < 40 && (
                    <p className="text-orange-700 dark:text-orange-400">
                      <strong>Lower Priority:</strong> Limited search volume or high competition. Best suited for comprehensive guides or knowledge base articles.
                    </p>
                  )}
                </div>
              </div>

              {/* Research in AEO Button */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    // Close dialog
                    setShowQuestionDialog(false);
                    // Navigate to AEO Research with question pre-filled
                    navigate('/research/aeo', {
                      state: {
                        question: selectedQuestion.text,
                        searchVolume: selectedQuestion.search_volume,
                        cpc: selectedQuestion.cpc,
                        competitors: selectedQuestion.top_domains,
                        relatedQuestions: selectedQuestion.paa_questions,
                      }
                    });
                    toast({
                      title: 'Navigating to AEO Research',
                      description: 'Auto-filling question data...',
                    });
                  }}
                  className="flex-1"
                  variant="default"
                >
                  <Target className="h-4 w-4 mr-2" />
                  Research in AEO
                </Button>
              </div>

              {/* Question Provenance */}
              <div className="pt-4 border-t">
                <QuestionProvenance question={selectedQuestion} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Progress Modal for Question Generation */}
      <QuestionGenerationProgressModal
        open={showProgressModal}
        onClose={handleProgressModalClose}
        onComplete={handleGenerationComplete}
        progressId={progressId}
        centerTerm={generatingTerm}
      />

      {/* Intent Clustering Results Dialog */}
      <Dialog open={showClusterDialog} onOpenChange={setShowClusterDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <GitBranch className="h-6 w-6 text-purple-600" />
              Intent Clustering Analysis
            </DialogTitle>
            <DialogDescription>
              Questions grouped by customer intent with opportunity value analysis
            </DialogDescription>
          </DialogHeader>

          {clusterAnalysis && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Intent Clusters</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{clusterAnalysis.intent_clusters?.length || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{clusterAnalysis.wheel?.total_questions || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Avg Cluster Size</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">
                      {clusterAnalysis.intent_clusters?.length
                        ? Math.round((clusterAnalysis.wheel?.total_questions || 0) / clusterAnalysis.intent_clusters.length)
                        : 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Total Opportunity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">
                      {clusterAnalysis.wheel?.total_search_volume?.toLocaleString() || 0}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Monthly searches</p>
                  </CardContent>
                </Card>
              </div>

              {/* Intent Clusters */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Intent Clusters (Sorted by Opportunity)</h3>
                {clusterAnalysis.intent_clusters?.map((cluster: any, index: number) => (
                  <Card key={index} className="border-l-4 border-l-purple-500">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-base">{cluster.label || `Cluster ${index + 1}`}</CardTitle>
                          <CardDescription className="mt-1">
                            {cluster.question_count} questions | Top sources: {Object.keys(cluster.sources || {}).slice(0, 3).join(', ') || 'Various'}
                          </CardDescription>
                        </div>
                        <div className="flex gap-6 ml-4">
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Questions</div>
                            <div className="text-2xl font-bold">{cluster.question_count || 0}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Search Volume</div>
                            <div className="text-2xl font-bold text-green-600">
                              {cluster.total_search_volume?.toLocaleString() || 0}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Avg CPC</div>
                            <div className="text-2xl font-bold text-blue-600">
                              ${cluster.avg_cpc?.toFixed(2) || '0.00'}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Opportunity</div>
                            <div className="text-2xl font-bold text-purple-600">
                              ${(cluster.total_opportunity_value || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Answer Types */}
                      {cluster.answer_types && Object.keys(cluster.answer_types).length > 0 && (
                        <div className="mb-3">
                          <Label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Answer Types Needed
                          </Label>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(cluster.answer_types).map(([type, count]: [string, any], idx: number) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {type}: {count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sample Questions */}
                      {cluster.questions && cluster.questions.length > 0 && (
                        <div>
                          <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                            Sample Questions (5 of {cluster.question_count})
                          </Label>
                          <div className="space-y-1.5">
                            {cluster.questions.slice(0, 5).map((q: any, idx: number) => (
                              <div key={idx} className="text-sm rounded bg-muted p-2 flex items-center justify-between">
                                <span className="flex-1">{q.text}</span>
                                <div className="flex gap-3 ml-4 text-xs text-muted-foreground shrink-0">
                                  <span>Vol: {q.search_volume?.toLocaleString() || 0}</span>
                                  <span>Score: {q.opportunity_score || 0}</span>
                                </div>
                              </div>
                            ))}
                            {cluster.question_count > 5 && (
                              <div className="text-xs text-muted-foreground italic mt-1">
                                + {cluster.question_count - 5} more questions in this cluster
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Export Clusters Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(clusterAnalysis, null, 2)], {
                      type: 'application/json',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `intent-clusters-${centerTerm}-${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    toast({
                      title: 'Exported',
                      description: 'Intent clusters exported as JSON',
                    });
                  }}
                  variant="outline"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Clusters (JSON)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Copy to Site Dialog - ENHANCED with Tone Customization */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy Wheel to Site
              <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
                NEW ⚡
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Copy "{wheelToCopy?.name}" to one of your sites with optional style customization
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="target-site">Select Target Site</Label>
              <Select
                value={selectedTargetSite}
                onValueChange={setSelectedTargetSite}
              >
                <SelectTrigger id="target-site" className="mt-2">
                  <SelectValue placeholder="Choose a site..." />
                </SelectTrigger>
                <SelectContent>
                  {userSites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name} ({site.url})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-medium">Style Customization (NEW)</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Rewrite questions to match your site's voice and language
              </p>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="copy-language">Language</Label>
                  <Select value={copyLanguage} onValueChange={setCopyLanguage}>
                    <SelectTrigger id="copy-language" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Spanish (Español)</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">German (Deutsch)</SelectItem>
                      <SelectItem value="fr">French (Français)</SelectItem>
                      <SelectItem value="it">Italian (Italiano)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="copy-style">Writing Style</Label>
                  <Select value={copyStyle} onValueChange={setCopyStyle}>
                    <SelectTrigger id="copy-style" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="as-is">Keep original (no changes)</SelectItem>
                      <SelectItem value="professional">Professional & Technical</SelectItem>
                      <SelectItem value="casual">Casual & Friendly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {copyStyle !== 'as-is' && (
                  <div className="bg-muted/50 p-3 rounded-md text-sm">
                    <div className="font-medium mb-1">Preview:</div>
                    <div className="text-muted-foreground">
                      Original: "What are the benefits of lightweight wheels?"
                    </div>
                    <div className="text-foreground mt-1">
                      {copyStyle === 'professional' && copyLanguage === 'es' &&
                        "¿Cuáles son las ventajas de las ruedas ligeras para ciclismo profesional?"}
                      {copyStyle === 'casual' && copyLanguage === 'es' &&
                        "Oye, ¿por qué debería importarme el peso de las ruedas?"}
                      {copyLanguage === 'en' && copyStyle === 'professional' &&
                        "What are the technical advantages of lightweight wheel systems?"}
                      {copyLanguage === 'en' && copyStyle === 'casual' &&
                        "Hey, why should I care about lightweight wheels?"}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {userSites.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No sites found. Please create a site first.
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCopyDialog(false)}
                disabled={isCopying}
              >
                Cancel
              </Button>
              <Button
                onClick={copyWheelToSite}
                disabled={!selectedTargetSite || isCopying}
                className="bg-primary"
              >
                {isCopying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {copyStyle !== 'as-is' ? 'Copying & Rewriting...' : 'Copying...'}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    {copyStyle !== 'as-is' ? 'Copy & Customize' : 'Copy Wheel'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map to Products Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              Map Questions to Products
            </DialogTitle>
            <DialogDescription>
              Create semantic mappings between "{wheelToMap?.name}" questions and your site's products
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="mapping-site">Select Site with Products</Label>
              <Select
                value={selectedMappingSite}
                onValueChange={setSelectedMappingSite}
              >
                <SelectTrigger id="mapping-site" className="mt-2">
                  <SelectValue placeholder="Choose a site..." />
                </SelectTrigger>
                <SelectContent>
                  {userSites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name} ({site.url})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="text-xs space-y-1 list-disc list-inside">
                <li>Uses AI embeddings to find semantically similar products</li>
                <li>Maps questions to top 5 most relevant products</li>
                <li>Similarity threshold: 50% or higher</li>
              </ul>
            </div>

            {userSites.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No sites found. Please create a site first.
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowMappingDialog(false)}
                disabled={isMapping}
              >
                Cancel
              </Button>
              <Button
                onClick={mapWheelToProducts}
                disabled={!selectedMappingSite || isMapping}
              >
                {isMapping ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Mapping...
                  </>
                ) : (
                  <>
                    <Network className="h-4 w-4 mr-2" />
                    Create Mappings
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Category Answers Dialog */}
      <Dialog open={showAnswerGenDialog} onOpenChange={setShowAnswerGenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Generate Category Answers
            </DialogTitle>
            <DialogDescription>
              Generate AI-powered answers for "{wheelToGenerateAnswers?.name}" questions using mapped products
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="answergen-site">Select Site with Product Mappings</Label>
              <Select
                value={selectedAnswerGenSite}
                onValueChange={setSelectedAnswerGenSite}
              >
                <SelectTrigger id="answergen-site" className="mt-2">
                  <SelectValue placeholder="Choose a site..." />
                </SelectTrigger>
                <SelectContent>
                  {userSites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name} ({site.url})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="text-xs space-y-1 list-disc list-inside">
                <li>Uses GPT-4o to generate comparison answers</li>
                <li>References top 5 mapped products per question</li>
                <li>Answers sent to Answer Approval interface</li>
                <li>Requires question-product mappings (run "Map to Products" first)</li>
              </ul>
            </div>

            {userSites.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No sites found. Please create a site first.
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAnswerGenDialog(false)}
                disabled={isGeneratingAnswers}
              >
                Cancel
              </Button>
              <Button
                onClick={generateCategoryAnswers}
                disabled={!selectedAnswerGenSite || isGeneratingAnswers}
              >
                {isGeneratingAnswers ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Generate Answers
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Product-Specific Question Generation Dialog */}
      <Dialog open={showProductQuestionsDialog} onOpenChange={setShowProductQuestionsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Generate Product-Specific Questions
              <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
                NEW ⚡
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Generate customized questions for each product in "{wheelForProductQuestions?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="border-b pb-3">
              <Label className="text-base font-medium">Product Selection</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="product-all"
                    checked={productSelection === 'all'}
                    onChange={() => setProductSelection('all')}
                    className="cursor-pointer"
                  />
                  <Label htmlFor="product-all" className="cursor-pointer font-normal">
                    All products in category (recommended)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="product-top-n"
                    checked={productSelection === 'top_n'}
                    onChange={() => setProductSelection('top_n')}
                    className="cursor-pointer"
                  />
                  <Label htmlFor="product-top-n" className="cursor-pointer font-normal flex items-center gap-2">
                    Top
                    <Input
                      type="number"
                      value={topNProducts}
                      onChange={(e) => setTopNProducts(Number(e.target.value))}
                      className="w-16 h-7"
                      disabled={productSelection !== 'top_n'}
                    />
                    products by sales
                  </Label>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-md text-sm space-y-2">
              <div className="font-medium">Workflow Steps:</div>
              <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
                <li>Phase A: Normalize product attributes across catalog</li>
                <li>Phase B: Rank products by perceived benefits</li>
                <li>Phase C: Map questions to relevant products</li>
                <li>Phase D: Reformulate questions with product details</li>
              </ul>
            </div>

            {isGeneratingProductQuestions && (
              <div className="space-y-2 p-3 bg-primary/5 rounded-md">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Generating...</span>
                  <span>{productQuestionProgress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${productQuestionProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowProductQuestionsDialog(false)}
                disabled={isGeneratingProductQuestions}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  // TODO: Implement product question generation
                  setIsGeneratingProductQuestions(true);
                  setProductQuestionProgress(0);

                  toast({
                    title: 'Starting Generation',
                    description: 'Product-specific questions are being generated...'
                  });

                  // Simulate progress (replace with actual API call)
                  const interval = setInterval(() => {
                    setProductQuestionProgress(prev => {
                      if (prev >= 100) {
                        clearInterval(interval);
                        setIsGeneratingProductQuestions(false);
                        setShowProductQuestionsDialog(false);
                        toast({
                          title: 'Generation Complete',
                          description: 'Product-specific questions have been generated'
                        });
                        return 100;
                      }
                      return prev + 10;
                    });
                  }, 500);
                }}
                disabled={isGeneratingProductQuestions}
                className="bg-primary"
              >
                {isGeneratingProductQuestions ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Questions
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Product Answer Generation Dialog */}
      <Dialog open={showProductAnswersDialog} onOpenChange={setShowProductAnswersDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Generate Product Answers
              <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
                NEW ⚡
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Generate AI-powered answers for product-specific questions in "{wheelForProductAnswers?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="border-b pb-3">
              <Label className="text-base font-medium">Answer Quality</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="quality-high"
                    checked={answerQuality === 'high'}
                    onChange={() => setAnswerQuality('high')}
                    className="cursor-pointer"
                  />
                  <Label htmlFor="quality-high" className="cursor-pointer font-normal">
                    High quality (GPT-4, slower, more expensive)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="quality-standard"
                    checked={answerQuality === 'standard'}
                    onChange={() => setAnswerQuality('standard')}
                    className="cursor-pointer"
                  />
                  <Label htmlFor="quality-standard" className="cursor-pointer font-normal">
                    Standard quality (GPT-3.5, faster, cheaper)
                  </Label>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-md text-sm space-y-2">
              <div className="font-medium">Cost Estimates:</div>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li>High quality: ~€0.004 per answer (detailed, accurate)</li>
                <li>Standard quality: ~€0.001 per answer (good, faster)</li>
              </ul>
              <div className="text-xs text-primary pt-1">
                Estimated total: ~€{answerQuality === 'high' ? '45' : '12'} for 11,000 answers
              </div>
            </div>

            {isGeneratingProductAnswers && (
              <div className="space-y-2 p-3 bg-primary/5 rounded-md">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Generating answers...</span>
                  <span>{productAnswerProgress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${productAnswerProgress}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  This may take 20-30 minutes for large catalogs
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowProductAnswersDialog(false)}
                disabled={isGeneratingProductAnswers}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  // TODO: Implement product answer generation
                  setIsGeneratingProductAnswers(true);
                  setProductAnswerProgress(0);

                  toast({
                    title: 'Starting Answer Generation',
                    description: `Using ${answerQuality === 'high' ? 'GPT-4' : 'GPT-3.5'} for answers...`
                  });

                  // Simulate progress (replace with actual API call)
                  const interval = setInterval(() => {
                    setProductAnswerProgress(prev => {
                      if (prev >= 100) {
                        clearInterval(interval);
                        setIsGeneratingProductAnswers(false);
                        setShowProductAnswersDialog(false);
                        toast({
                          title: 'Answers Generated',
                          description: 'Product answers are ready for review'
                        });
                        return 100;
                      }
                      return prev + 5;
                    });
                  }, 1000);
                }}
                disabled={isGeneratingProductAnswers}
                className="bg-primary"
              >
                {isGeneratingProductAnswers ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating Answers...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Generate Answers
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
